/* eslint-disable callback-return */
/* eslint-disable no-duplicate-imports */
import axios, { isAxiosError } from "axios";
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig
} from "axios";

// --- Custom Error Class ---

/**
 * Custom error class for HTTP requests that provides additional context
 * and helper methods for error handling.
 *
 * @example
 * ```typescript
 * try {
 *   await httpService.request({ url: "/api/data" });
 * } catch (error) {
 *   if (error instanceof HttpServiceError) {
 *     console.log(error.status, error.url, error.method);
 *   }
 * }
 * ```
 */
export class HttpServiceError extends Error {
  /** HTTP status code (e.g., 401, 404, 500) */
  public readonly status?: number;
  /** The URL that was requested */
  public readonly url?: string;
  /** HTTP method used (GET, POST, etc.) */
  public readonly method?: string;
  /** Axios error code (e.g., ERR_NETWORK, ECONNABORTED) */
  public readonly code?: string;
  /** Original Axios response object */
  public readonly response?: AxiosResponse;

  constructor(error: AxiosError) {
    const message = error.response?.data ?
      JSON.stringify(error.response.data) :
      error.message;

    super(message);
    this.name = "HttpServiceError";
    this.status = error.response?.status;
    this.url = error.config?.url;
    this.method = error.config?.method?.toUpperCase();
    this.code = error.code;
    this.response = error.response;
  }
}

// --- Types ---

/**
 * Extended request configuration with additional HttpService options.
 */
interface HttpServiceRequestConfig extends AxiosRequestConfig {
  /** URL path variables to replace (e.g., { id: 123 } replaces {id} in URL) */
  urlVariables?: Record<string, string | number>;
}

/**
 * Configuration options for HttpService instance.
 */
interface HttpServiceConfig extends AxiosRequestConfig {
  /** Transform AxiosError to HttpServiceError (default: true) */
  enableErrorTransform?: boolean;
  /** Enable debug logging for requests/responses (default: false) */
  debug?: boolean;
  /** Custom logger function for debug mode */
  logger?: (message: string, data?: unknown) => void;
  /** Default timeout for all requests in ms (default: 30000) */
  defaultTimeout?: number;
}

// --- HttpService Class ---

/**
 * A robust HTTP client wrapper around Axios that provides:
 * - URL variable substitution
 * - Custom error handling
 * - Debug logging
 * - Interceptor management with keys
 *
 * @example
 * ```typescript
 * // Create an instance
 * const http = new HttpService({
 *   baseURL: "https://api.example.com",
 *   defaultTimeout: 10000,
 *   debug: true
 * });
 *
 * // Basic request
 * const response = await http.request({
 *   url: "/users/{id}",
 *   method: "GET",
 *   urlVariables: { id: 123 }
 * });
 *
 * ```
 */
class HttpService {
  private instance: AxiosInstance;
  private requestInterceptors: Map<string, number>;
  private responseInterceptors: Map<string, number>;
  private enableErrorTransform: boolean;
  private debug: boolean;
  private logger: (message: string, data?: unknown) => void;
  private defaultTimeout: number;

  /**
   * Creates a new HttpService instance.
   *
   * @param config - Configuration options extending AxiosRequestConfig
   * @param config.enableErrorTransform - Transform errors to HttpServiceError (default: true)
   * @param config.debug - Enable debug logging (default: false)
   * @param config.logger - Custom logger function (default: console.log)
   * @param config.defaultTimeout - Default request timeout in ms (default: 30000)
   */
  constructor(config: HttpServiceConfig = {}) {
    const {
      enableErrorTransform = true,
      debug = false,
      logger = console.log,
      defaultTimeout = 30000,
      ...axiosConfig
    } = config;

    this.instance = axios.create({
      timeout: defaultTimeout,
      ...axiosConfig
    });
    this.requestInterceptors = new Map();
    this.responseInterceptors = new Map();
    this.enableErrorTransform = enableErrorTransform;
    this.debug = debug;
    this.logger = logger;
    this.defaultTimeout = defaultTimeout;
  }

  // --- Core Request Method ---

  /**
   * Makes an HTTP request with support for retries, deduplication, and URL variables.
   *
   * @template T - Expected response data type
   * @template R - Full response type (default: AxiosResponse<T>)
   * @param config - Request configuration
   * @returns Promise resolving to the response
   * @throws {HttpServiceError} When request fails (if enableErrorTransform is true)
   *
   * @example
   * ```typescript
   * // GET request with URL variables
   * const response = await http.request<User>({
   *   url: "/users/{id}",
   *   method: "GET",
   *   urlVariables: { id: 123 }
   * });
   *
   * // POST request
   * const response = await http.request<CreateResponse>({
   *   url: "/orders",
   *   method: "POST",
   *   data: { items: [...] }
   * });
   * ```
   */
  public request = async <T = unknown, R = AxiosResponse<T>>(
    config: HttpServiceRequestConfig
  ): Promise<R> => {
    return this.executeRequest<T, R>(this.processUrlVariables(config));
  };

  private executeRequest = async <T = unknown, R = AxiosResponse<T>>(
    finalConfig: AxiosRequestConfig
  ): Promise<R> => {
    const startTime = Date.now();

    this.log("Request started", {
      method: finalConfig.method || "GET",
      url: finalConfig.url,
      params: finalConfig.params
    });

    try {
      const response = await this.instance.request<T, R>(finalConfig);

      this.log("Request completed", {
        method: finalConfig.method || "GET",
        url: finalConfig.url,
        status: (response as AxiosResponse).status,
        duration: `${Date.now() - startTime}ms`
      });

      return response;
    }
    catch (error) {
      if (isAxiosError(error)) {
        this.log("Request failed", {
          method: finalConfig.method || "GET",
          url: finalConfig.url,
          status: error.response?.status,
          code: error.code,
          duration: `${Date.now() - startTime}ms`
        });

        // Transform to custom error
        if (this.enableErrorTransform) {
          throw new HttpServiceError(error);
        }
      }
      throw error;
    }
  };

  private log(message: string, data?: unknown): void {
    if (this.debug) {
      this.logger(`[HttpService] ${message}`, data);
    }
  }

  // --- URL Variable Processing ---

  private processUrlVariables = (
    config: HttpServiceRequestConfig
  ): AxiosRequestConfig => {
    const { urlVariables, ...restConfig } = config;

    if (!urlVariables || !config.url) {
      return restConfig;
    }

    let url = String(config.url);

    Object.entries(urlVariables).forEach(([ key, value ]) => {
      const placeholder = `{${key}}`;
      if (!url.includes(placeholder)) {
        console.warn(
          `[HttpService] URL variable "${key}" not found in URL: ${url}`
        );
      }
      url = url.replace(placeholder, encodeURIComponent(String(value)));
    });

    // Warn if there are remaining placeholders
    const remainingPlaceholders = url.match(/\{[^}]+\}/g);
    if (remainingPlaceholders) {
      console.warn(
        `[HttpService] Unresolved URL variables: ${remainingPlaceholders.join(
          ", "
        )}`
      );
    }

    return {
      ...restConfig,
      url
    };
  };

  // --- Header Management ---

  /**
   * Sets a default header for all requests.
   *
   * @param key - Header name
   * @param value - Header value
   */
  public setHeader = (key: string, value: string): void => {
    this.instance.defaults.headers.common[key] = value;
  };

  /**
   * Gets a default header value.
   *
   * @param key - Header name
   * @returns Header value or undefined
   */
  public getHeader = (key: string): string | undefined => {
    const value = this.instance.defaults.headers.common[key];
    return typeof value === "string" ? value : undefined;
  };

  /**
   * Removes a default header.
   *
   * @param key - Header name to remove
   */
  public removeHeader = (key: string): void => {
    delete this.instance.defaults.headers.common[key];
  };

  // --- Interceptor Management ---

  /**
   * Adds a request interceptor with a unique key. If an interceptor with the
   * same key exists, it will be removed first.
   *
   * @param key - Unique identifier for this interceptor
   * @param onFulfilled - Function to transform the request config
   * @param onRejected - Function to handle request errors
   */
  public addRequestInterceptor = (
    key: string,
    onFulfilled?: (
      config: InternalAxiosRequestConfig
    ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>,
    onRejected?: (error: unknown) => unknown
  ): void => {
    this.ejectRequestInterceptor(key);
    const id = this.instance.interceptors.request.use(onFulfilled, onRejected);
    this.requestInterceptors.set(key, id);
    this.log("Added request interceptor", { key });
  };

  /**
   * Adds a response interceptor with a unique key. If an interceptor with the
   * same key exists, it will be removed first.
   *
   * @param key - Unique identifier for this interceptor
   * @param onFulfilled - Function to transform the response
   * @param onRejected - Function to handle response errors
   */
  public addResponseInterceptor = (
    key: string,
    onFulfilled?: (
      response: AxiosResponse
    ) => AxiosResponse | Promise<AxiosResponse>,
    onRejected?: (error: unknown) => unknown
  ): void => {
    this.ejectResponseInterceptor(key);
    const id = this.instance.interceptors.response.use(onFulfilled, onRejected);
    this.responseInterceptors.set(key, id);
    this.log("Added response interceptor", { key });
  };

  /**
   * Removes a request interceptor by its key.
   *
   * @param key - The interceptor key to remove
   */
  public ejectRequestInterceptor = (key: string): void => {
    const id = this.requestInterceptors.get(key);
    if (id !== undefined) {
      this.instance.interceptors.request.eject(id);
      this.requestInterceptors.delete(key);
      this.log("Ejected request interceptor", { key });
    }
  };

  /**
   * Removes a response interceptor by its key.
   *
   * @param key - The interceptor key to remove
   */
  public ejectResponseInterceptor = (key: string): void => {
    const id = this.responseInterceptors.get(key);
    if (id !== undefined) {
      this.instance.interceptors.response.eject(id);
      this.responseInterceptors.delete(key);
      this.log("Ejected response interceptor", { key });
    }
  };

  // --- Utility Methods ---

  /**
   * Returns the underlying Axios instance for advanced use cases.
   *
   * @returns The Axios instance
   */
  public getAxiosInstance = (): AxiosInstance => {
    return this.instance;
  };

  /**
   * Gets the configured base URL.
   *
   * @returns Base URL or undefined
   */
  public getBaseUrl = (): string | undefined => {
    return this.instance.defaults.baseURL;
  };

  /**
   * Gets the default timeout value.
   *
   * @returns Timeout in milliseconds
   */
  public getDefaultTimeout = (): number => {
    return this.defaultTimeout;
  };

  /**
   * Enables or disables debug mode at runtime.
   *
   * @param enabled - Whether debug mode should be enabled
   */
  public setDebugMode = (enabled: boolean): void => {
    this.debug = enabled;
  };

  /**
   * Checks if debug mode is enabled.
   *
   * @returns true if debug mode is enabled
   */
  public isDebugMode = (): boolean => {
    return this.debug;
  };

}

export default HttpService;
export type { HttpServiceRequestConfig, HttpServiceConfig };
